<script setup lang="ts">
import { parseISO } from 'date-fns';
import { computed, ref } from 'vue';

import { STREAM_BASE_URI } from '@/js/constants';
import { useTemplate } from '@/store/templateData';
import { formatDateOrEmpty } from '@/utils/transformer/time';
import IconHumbleiconsTimes from '~icons/humbleicons/times';
import IconMdiCalendarPlus from '~icons/mdi/calendar-plus';

import CalendarDialog from '../CalendarDialog/CalendarDialog.vue';

defineOptions({
  name: 'ModuleUpcomingLiveFabDisplay'
});

const {
  isUpcoming,
  handsupConfig,
  currentStreamStatus,
} = useTemplate();

const isComponentActive = ref(true);

const needRender = computed(() => [
  isUpcoming.value,
  isComponentActive.value,
].every(Boolean));

const imgUrl = computed(() => currentStreamStatus.value?.image?.[0]?.url ?? '');

const upcomingAt = computed(() => currentStreamStatus.value?.upcoming_at);
const upcomingAtDisplay = computed(() =>
  upcomingAt.value ? formatDateOrEmpty(parseISO(upcomingAt.value), 'LL/dd HH:mm') : '');

const titleDisplay = computed(() => {
  return currentStreamStatus.value?.upcoming_title || currentStreamStatus.value?.title;
});

const upcomingLiveUrl = computed(() => {
  const post = handsupConfig.value?.post;
  const store = handsupConfig.value?.store;

  return post?.share_url ?? `${STREAM_BASE_URI}/${store?.slug}/live/${post?.id}`;
});

const isCalendarDialogOpened = ref(false);
const openCalendarDialog = () => {
  isCalendarDialogOpened.value = true;
};

const closeCalendarDialog = () => {
  isCalendarDialogOpened.value = false;
};

const hideComponentForever = () => {
  isComponentActive.value = false;
};

</script>

<template>
<div class="module-upcoming-live-fab-display" v-if="needRender">
  <div class="module-upcoming-live-fab-display__window">
    <div class="module-upcoming-live-fab-display__image-box" v-if="imgUrl">
      <img :src="imgUrl"/>
    </div>

    <div class="module-upcoming-live-fab-display__text-box">
      <h2 class="module-upcoming-live-fab-display__heading">{{ $t("cms_setting.display.upcoming_live_fab_heading") }}</h2>
      <h3 class="module-upcoming-live-fab-display__title">{{ titleDisplay }}</h3>
      <button
        class="module-upcoming-live-fab-display__calendar-btn"
        v-if="upcomingAt"
        type="button"
        @click="openCalendarDialog"
      >
        <time>{{ upcomingAtDisplay }}</time>
        <IconMdiCalendarPlus class="module-upcoming-live-fab-display__calendar-btn__icon"/>
      </button>
    </div>

    <button
      class="module-upcoming-live-fab-display__close-btn"
      type="button"
      @click="hideComponentForever"
    >
      <IconHumbleiconsTimes class="text-base"/>
    </button>
  </div>

  <CalendarDialog
    v-if="isCalendarDialogOpened"
    :title="titleDisplay ?? ''"
    :shareUrl="upcomingLiveUrl"
    :startDate="upcomingAt ?? undefined"
    @close="closeCalendarDialog"
  />
</div>
</template>
<style lang="scss" scoped>
@use "sass:math";

$mobile-module-fixed-height: 140px;
$mobile-module-fixed-width: 375px - 12px * 2;
$mobile-image-box-width: 65.25px;
$desktop-module-fixed-height: 246px;
$desktop-module-fixed-width: 375px;
$desktop-image-box-width: 116px;

.module-upcoming-live-fab-display {
  @apply fixed right-3 bottom-3 left-3 mx-auto z-9999;
  @apply bg-black text-white;
  @apply shadow rounded;

  width: $mobile-module-fixed-width;
  height: $mobile-module-fixed-height;
  padding: 0 !important;

  &__window {
    @apply relative p-3 flex h-full;
  }

  &__image-box {
    @apply mr-6;

    width: $mobile-image-box-width;
    height: $mobile-image-box-width * math.div(16, 9);

    img {
      @apply rounded-lg object-cover w-full h-full;
    }
  }

  &__text-box {
    flex: 1;

    @apply flex flex-col;
  }

  &__heading {
    @apply font-bold text-base mb-1;
  }

  &__title {
    @apply text-xs flex-1;
    @apply flex items-center;
  }

  &__calendar-btn {
    @apply flex items-center justify-center;
    @apply py-1 border border-solid rounded-full outline-none;
    @apply text-sm font-bold;
    @apply text-white border-white bg-black fill-white;

    @include rwd-hover {
      @apply text-black border-black bg-white fill-black;
    }

    &__icon {
      @apply inline-block ml-2;
    }
  }

  &__close-btn {
    @apply absolute top-3 right-3 cursor-pointer w-6 h-6 text-base;
    @apply flex justify-center items-center;
    @apply bg-opacity-20 rounded-full border;
    @apply text-white border-white bg-black;

    -webkit-tap-highlight-color: transparent;

    @include rwd-hover {
      @apply text-black border-black bg-white;
    }
  }
}

// tablet
.tablet .module-upcoming-live-fab-display {
  @apply right-5 bottom-5;

  margin-right: 0 !important;
  width: $desktop-module-fixed-width;
  height: $desktop-module-fixed-height;

  &__window {
    @apply p-5;
  }

  &__image-box {
    width: $desktop-image-box-width;
    height: $desktop-image-box-width * math.div(16, 9);
  }

  &__title {
    @apply text-base;
  }

  &__heading {
    @apply mt-5 text-xl;
  }

  &__calendar-btn {
    @apply mb-1;
  }
}

// PC - 1024px
.laptop .module-upcoming-live-fab-display {
  @apply right-5 bottom-5;

  margin-right: 0 !important;
  width: $desktop-module-fixed-width;
  height: $desktop-module-fixed-height;

  &__window {
    @apply p-5;
  }

  &__image-box {
    width: $desktop-image-box-width;
    height: $desktop-image-box-width * math.div(16, 9);
  }

  &__title {
    @apply text-base;
  }

  &__heading {
    @apply mt-5 text-xl;
  }

  &__calendar-btn {
    @apply mb-1;
  }
}

// PC - 1440px
.desktop .module-upcoming-live-fab-display {
  @apply right-5 bottom-5;

  margin-right: 0 !important;
  width: $desktop-module-fixed-width;
  height: $desktop-module-fixed-height;

  &__window {
    @apply p-5;
  }

  &__image-box {
    width: $desktop-image-box-width;
    height: $desktop-image-box-width * math.div(16, 9);
  }

  &__title {
    @apply text-base;
  }

  &__heading {
    @apply mt-5 text-xl;
  }

  &__calendar-btn {
    @apply mb-1;
  }
}
</style>
